import React from 'react';
import Text from '../../../Components/Base/Text/Text';
import Tooltip from '../../../Components/Popup/Tooltip';
import useTranslation from '../../../Hooks/useTranslation';

export default function useCalendarEvent() {
	const { t } = useTranslation();
	const renderAbsenceEvent = (event: any) => {
		const eventData = {
			title: event?.extendedProps?.shift?.label,
			doctor: event?.extendedProps?.doctor?.name,
			note: event?.extendedProps?.note,
			createdBy: event?.extendedProps?.createdBy?.name,
		};

		return (
			<div
				className={'custom-event'}
				style={{ backgroundColor: event.backgroundColor }}>
				<div className={'custom-event__title'}>{eventData.title}</div>
				{eventData.doctor && (
					<div className={'custom-event__info'}>
						<Text tx={'base.paramedic'} inline firstUpper />:{' '}
						<span className='custom-event__info__value'>
							{eventData.doctor}
						</span>
					</div>
				)}
				{eventData.createdBy && (
					<div className={'custom-event__info'}>
						<Text tx={'base.sentBy'} inline firstUpper />:{' '}
						<span className='custom-event__info__value'>
							{eventData.createdBy}
						</span>
					</div>
				)}
				{eventData.note && (
					<div className={'custom-event__info'}>
						<Text tx={'component.absenceCalendar.reason'} inline firstUpper />:{' '}
						<span className='custom-event__info__value'>{eventData.note}</span>
					</div>
				)}
			</div>
		);
	};

	const renderOnlineOrderingEvent = (event: any) => {
		const caseTypesText = () => {
			const title =
				'component.workplaceOrderingSettingsCalendar.caseTypeInfo.text1';
			let unit = '';
			const length = eventData.caseTypes.length;
			if (length === 1) {
				unit =
					'component.workplaceOrderingSettingsCalendar.caseTypeInfo.case.one';
			} else if (length > 1 && length < 5) {
				unit =
					'component.workplaceOrderingSettingsCalendar.caseTypeInfo.case.two';
			} else {
				unit =
					'component.workplaceOrderingSettingsCalendar.caseTypeInfo.case.many';
			}

			return (
				<div>
					{t(title)}:{' '}
					<span>
						{length} {t(unit)}
					</span>
				</div>
			);
		};

		if (event?.extendedProps?.shift?.type !== 'ONLINE_ORDER') {
			return renderServiceScheduleEvent(event);
		}

		const eventData = {
			title: t('component.workplaceOrderingSettingsCalendar.eventTitle'),
			timeInterval: event?.extendedProps?.shift?.timeInterval,
			caseTypes: event?.extendedProps?.caseTypes,
		};

		const eventTitle = (color: string) => (
			<div className={'custom-event__title'} style={{ color, fontWeight: 400 }}>
				{eventData.title} <span>{eventData.timeInterval}</span>
			</div>
		);

		const customTooltip = (trigger: React.ReactElement) => {
			return (
				<Tooltip trigger={trigger}>
					{eventTitle('#fff')}
					<hr style={{ borderColor: 'rgba(255,255,255,0.4)', margin: '5px' }} />
					<div
						className={'custom-event__caseTypeTitle'}
						style={{ color: '#fff' }}>
						{t(
							'component.workplaceOrderingSettingsCalendar.caseTypeInfo.text1',
						)}
						:
					</div>
					{eventData.caseTypes &&
						eventData.caseTypes.map((item: any, key: string) => (
							<div className={'custom-event__info'} key={key}>
								<span className='custom-event__caseType'>
									{item.label}{' '}
									{item.eventInterval && (
										<>
											({t('component.useCalendarEvent.after')}{' '}
											{item.eventInterval}{' '}
											{t('component.useCalendarEvent.minuteShort')})
										</>
									)}
								</span>
							</div>
						))}
				</Tooltip>
			);
		};

		return (
			<div
				className={'custom-event'}
				style={{ backgroundColor: event.backgroundColor, padding: '10px' }}>
				{customTooltip(eventTitle('#000'))}
				<hr style={{ margin: '10px 0' }} />
				{customTooltip(
					<div
						className={'custom-event__caseTypeTitle'}
						style={{ color: '#000' }}>
						{caseTypesText()}
					</div>,
				)}
			</div>
		);
	};

	const renderServiceScheduleEvent = (event: any) => {
		return (
			<div
				className={'office-hours flex-column'}
				style={{ backgroundColor: event.backgroundColor }}>
				<div className={'d-flex flex-column'}>
					{event.extendedProps.workers.map((item, index) => (
						<Tooltip
							key={index}
							position='bottom center'
							trigger={
								<div className='office-hours__name'>
									<div>{item.name}</div>
									<div>{event?.extendedProps?.shift?.timeInterval}</div>
								</div>
							}>
							<div>
								<div>
									<span>
										{item.role.map((item, index2) => {
											return (
												<React.Fragment key={index2}>
													{index2 > 0 && <>, </>}
													{item?.role?.name}
												</React.Fragment>
											);
										})}
										:
									</span>{' '}
									{item.name}
								</div>
								{event.extendedProps.shift && (
									<div className={'office-hours__role'}>
										{event?.extendedProps?.shift.label}
									</div>
								)}
							</div>
						</Tooltip>
					))}
					{event.extendedProps.workers.length < 1 && (
						<div className='office-hours__name'>
							{event?.extendedProps?.shift?.label}
						</div>
					)}
				</div>
			</div>
		);
	};

	return { renderAbsenceEvent, renderOnlineOrderingEvent };
}
